/**
 * Composer "Insert link" control: a toolbar button + small popover (Text, URL)
 * that writes `[text](url)` at the caret / over the selection, the
 * Cmd/Ctrl+K shortcut (only while the composer input is focused), and
 * paste-a-URL-over-selection wrapping. See composerLinks.ts.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'lucide-react';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
  COMPOSER_CLAIMED_SHORTCUTS,
  insertLink,
  isInsertLinkShortcut,
  normalizeLinkUrl,
  wrapSelectionOnPaste,
} from './composerLinks';

type TextField = HTMLInputElement | HTMLTextAreaElement;

export interface ComposerLinkState {
  open: boolean;
  initialText: string;
  openDialog: () => void;
  close: () => void;
  insert: (text: string, url: string) => void;
  /** Spread onto the composer input. */
  inputProps: {
    onKeyDown: (e: React.KeyboardEvent<TextField>) => boolean;
    onPaste: (e: React.ClipboardEvent<TextField>) => void;
    'data-claims-shortcuts': string;
  };
}

export function useComposerLink(
  inputRef: React.RefObject<TextField | null>,
  value: string,
  setValue: (next: string) => void,
): ComposerLinkState {
  const [open, setOpen] = useState(false);
  const [initialText, setInitialText] = useState('');
  const range = useRef<{ start: number; end: number }>({ start: 0, end: 0 });

  const placeCaret = useCallback(
    (pos: number) => {
      requestAnimationFrame(() => {
        const el = inputRef.current;
        if (!el) return;
        el.focus();
        try {
          el.setSelectionRange(pos, pos);
        } catch {
          /* some input types don't support selection */
        }
      });
    },
    [inputRef],
  );

  const openDialog = useCallback(() => {
    const el = inputRef.current;
    const start = el?.selectionStart ?? value.length;
    const end = el?.selectionEnd ?? start;
    range.current = { start: Math.min(start, end), end: Math.max(start, end) };
    setInitialText(value.slice(range.current.start, range.current.end));
    setOpen(true);
  }, [inputRef, value]);

  const close = useCallback(() => {
    setOpen(false);
    placeCaret(range.current.end);
  }, [placeCaret]);

  const insert = useCallback(
    (text: string, url: string) => {
      const edit = insertLink(value, range.current.start, range.current.end, text, url);
      setValue(edit.value);
      setOpen(false);
      placeCaret(edit.cursor);
    },
    [value, setValue, placeCaret],
  );

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent<TextField>) => {
      if (!isInsertLinkShortcut(e)) return false;
      e.preventDefault();
      e.stopPropagation();
      openDialog();
      return true;
    },
    [openDialog],
  );

  const onPaste = useCallback(
    (e: React.ClipboardEvent<TextField>) => {
      const el = e.currentTarget;
      const start = el.selectionStart ?? 0;
      const end = el.selectionEnd ?? start;
      const edit = wrapSelectionOnPaste(el.value, start, end, e.clipboardData?.getData('text/plain') ?? '');
      if (!edit) return;
      e.preventDefault();
      setValue(edit.value);
      placeCaret(edit.cursor);
    },
    [setValue, placeCaret],
  );

  return {
    open,
    initialText,
    openDialog,
    close,
    insert,
    inputProps: { onKeyDown, onPaste, 'data-claims-shortcuts': COMPOSER_CLAIMED_SHORTCUTS },
  };
}

interface InsertLinkControlProps {
  link: ComposerLinkState;
  disabled?: boolean;
}

/** Toolbar button + anchored popover. Place inside the composer toolbar. */
export function InsertLinkControl({ link, disabled }: InsertLinkControlProps) {
  const { t } = useAppTranslation();
  const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent);
  const shortcut = isMac ? '⌘K' : 'Ctrl+K';
  const label = t('messaging.composer.insertLink', 'Insert link');

  return (
    <div className="relative">
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="text-muted-foreground"
        // Keep the popover's outside-click handler from closing it just before
        // this click toggles it.
        onMouseDown={(e) => e.stopPropagation()}
        onClick={() => (link.open ? link.close() : link.openDialog())}
        disabled={disabled}
        title={`${label} (${shortcut})`}
        aria-label={label}
        aria-expanded={link.open}
      >
        <Link className="h-5 w-5" />
      </Button>
      {link.open && <InsertLinkPopover initialText={link.initialText} onCancel={link.close} onInsert={link.insert} />}
    </div>
  );
}

interface InsertLinkPopoverProps {
  initialText: string;
  onCancel: () => void;
  onInsert: (text: string, url: string) => void;
}

export function InsertLinkPopover({ initialText, onCancel, onInsert }: InsertLinkPopoverProps) {
  const { t } = useAppTranslation();
  const [text, setText] = useState(initialText);
  const [url, setUrl] = useState('');
  const [error, setError] = useState<string | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const textRef = useRef<HTMLInputElement>(null);
  const urlRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    // Selection already gives the text → jump straight to the URL.
    (initialText ? urlRef : textRef).current?.focus();
  }, [initialText]);

  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) onCancel();
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [onCancel]);

  const submit = () => {
    const href = normalizeLinkUrl(url);
    if (!href) {
      setError(t('messaging.composer.linkInvalid', 'Enter a web address starting with http:// or https://, or a domain like example.org.'));
      urlRef.current?.focus();
      return;
    }
    onInsert(text, href);
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      submit();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      onCancel();
    }
  };

  return (
    <div
      ref={panelRef}
      role="dialog"
      aria-label={t('messaging.composer.insertLink', 'Insert link')}
      className="absolute bottom-full left-0 z-30 mb-2 w-80 space-y-2 rounded-md border bg-white p-3 shadow-lg dark:border-slate-700 dark:bg-slate-900"
      onKeyDown={onKeyDown}
    >
      <label className="block text-xs font-medium text-muted-foreground">
        {t('messaging.composer.linkText', 'Text')}
        <Input
          ref={textRef}
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={t('messaging.composer.linkTextPlaceholder', 'Text to display (optional)')}
          className="mt-1"
        />
      </label>
      <label className="block text-xs font-medium text-muted-foreground">
        {t('messaging.composer.linkUrl', 'URL')}
        <Input
          ref={urlRef}
          value={url}
          onChange={(e) => {
            setUrl(e.target.value);
            setError(null);
          }}
          placeholder="https://"
          inputMode="url"
          aria-invalid={!!error}
          className="mt-1"
        />
      </label>
      {error && (
        <p className="text-xs text-destructive" role="alert">
          {error}
        </p>
      )}
      <div className="flex justify-end gap-2 pt-1">
        <Button type="button" variant="outline" size="sm" onClick={onCancel}>
          {t('messaging.channel.cancel', 'Cancel')}
        </Button>
        <Button type="button" size="sm" onClick={submit} disabled={!url.trim()}>
          {t('messaging.composer.linkInsert', 'Insert')}
        </Button>
      </div>
    </div>
  );
}
